import React, { useState } from 'react';
import { Todo } from '../types/Todo';

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (mergeMode: boolean) => void;
  importedTodos: Todo[];
  existingCount: number;
}

const ImportDialog: React.FC<ImportDialogProps> = ({
  isOpen,
  onClose,
  onImport,
  importedTodos,
  existingCount
}) => {
  const [selectedMode, setSelectedMode] = useState<'merge' | 'replace'>('merge');

  if (!isOpen) return null;

  // Duplicate count will be calculated in the parent component

  const handleImport = () => {
    onImport(selectedMode === 'merge');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-bounce-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Import Tasks
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            You're about to import <strong>{importedTodos.length} tasks</strong>.
            You currently have <strong>{existingCount} existing tasks</strong>.
          </p>

          <div className="space-y-3">
            <div 
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                selectedMode === 'merge' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedMode('merge')}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  selectedMode === 'merge' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}>
                  {selectedMode === 'merge' && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Merge with Existing Tasks</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Add imported tasks to your current task list. Duplicate tasks (same ID) will be skipped.
                  </p>
                  <div className="mt-2 text-xs text-green-600 font-medium">
                    ✅ Recommended - Safe option
                  </div>
                </div>
              </div>
            </div>

            <div 
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                selectedMode === 'replace' 
                  ? 'border-red-500 bg-red-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedMode('replace')}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  selectedMode === 'replace' ? 'border-red-500 bg-red-500' : 'border-gray-300'
                }`}>
                  {selectedMode === 'replace' && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Replace All Tasks</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Replace all existing tasks with imported ones. This will delete your current tasks.
                  </p>
                  <div className="mt-2 text-xs text-red-600 font-medium">
                    ⚠️ Warning - Will delete existing tasks
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleImport}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
              selectedMode === 'merge'
                ? 'bg-blue-500 hover:bg-blue-600 text-white'
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            {selectedMode === 'merge' ? 'Merge Tasks' : 'Replace Tasks'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportDialog;
